import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CopyArrayPipe } from '@new/common/pipes/copy-array.pipe';
import { SearchInputComponent } from '@new/common/search-input/search-input.component';
import { RemoteData } from '@new/common/utils/remote-data';
import { TableWrapperComponent } from '@new/pools/pool/lib/components/man-table/man-table-wrapper/table-wrapper.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { TableModule } from 'primeng/table';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { PoolListElementFragment } from '../../../../admin/services/load-pools.generated';
import { FuzzySearchService } from '../../../../common/services/fuzzy-search.service';
import { PoolsState } from '../../../state/pools';
import { Pool } from '../../types/pools';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pool-list.component.html',
  host: {
    class: 'content',
    style: 'display:flex; margin: 1.5rem; flex-direction: column;'
  },
  standalone: true,
  imports: [
    SearchInputComponent,
    TableWrapperComponent,
    TableModule,
    CommonModule,
    CopyArrayPipe,
    TranslateModule
  ]
})
export class PoolListComponent {
  public readonly filterFields: (keyof Pool)[] = ['id', 'name'];
  public totalPoolCount: string;
  public filteredPoolCount: string;

  public readonly pools$: Observable<RemoteData<PoolListElementFragment[]>>;

  private searchStringSource = new BehaviorSubject<string>('');
  private searchString$ = this.searchStringSource.asObservable();

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private fuzzySearchService: FuzzySearchService<PoolListElementFragment>
  ) {
    this.pools$ = combineLatest([
      this.store.select(PoolsState.pools),
      this.searchString$
    ]).pipe(
      map(([pools, searchString]) => {
        const result = this.fuzzySearchService.search(
          pools.data || [],
          searchString,
          this.filterFields,
          'name'
        );

        this.totalPoolCount = (
          pools.data ? pools.data.length : 0
        ).toLocaleString();
        this.filteredPoolCount = result.length.toLocaleString();

        return { ...pools, data: result };
      })
    );
  }

  public onSelectPool(poolId: PoolListElementFragment['id']): void {
    this.router.navigate(['./', poolId, 'questions'], {
      relativeTo: this.route
    });
  }

  public onSearch(value: string): void {
    this.searchStringSource.next(value);
  }

  public onSearchClear(): void {
    this.searchStringSource.next('');
  }
}
